import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Fab, Button, TextField, Typography, InputAdornment, Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { Label } from 'src/components/label';
import { AuthorizationLevel } from 'src/types/authorization';
import type { SavedViewsController } from 'src/components/saved-views/use-saved-views';
import type { SavedViewPageKey } from 'src/components/saved-views/types';

import { Iconify } from '../iconify';
import { FilterDropdown } from '../filter-dropdown';

import type { FilterOption, SelectedOptions } from '../filter-dropdown';

const DEFAULT_FILTER_CHIP_MAX_WIDTH = 200;

interface Props {
  options?: FilterOption;
  selectedOptions?: SelectedOptions[];
  onFiltersChange?: (newValue: SelectedOptions[], touchedFields?: string[]) => void;
  searchValue?: string;
  onValueChange?: (newValue: string) => void;
  enableSearch?: boolean;
  enableFilter?: boolean;
  /** When true, each multi-select filter field shows Select all / Clear all controls. */
  enableFieldBulkActions?: boolean;
  containerSx?: SxProps<Theme>;
  searchFieldSx?: SxProps<Theme>;
  pageKey?: SavedViewPageKey;
  savedViews?: SavedViewsController;
}

export const Filters = ({
  options,
  selectedOptions,
  onFiltersChange,
  searchValue,
  onValueChange,
  enableSearch = false,
  enableFilter = true,
  enableFieldBulkActions = false,
  containerSx,
  searchFieldSx,
  pageKey: _pageKey,
  savedViews: _savedViews,
}: Props) => {
  const { t } = useTranslation(['common', 'organization']);
  const [filters, setFilters] = useState<SelectedOptions[]>(selectedOptions || []);

  const formatAuthorizationLevel = useCallback(
    (level: AuthorizationLevel) => t(`organization:project-roles.levels.${level}`),
    [t]
  );

  const emitFiltersChange = useCallback(
    (nextFilters: SelectedOptions[], touchedFields?: string[]) => {
      setFilters(nextFilters);
      onFiltersChange?.(nextFilters, touchedFields);
    },
    [onFiltersChange]
  );

  const onOptionDelete = useCallback(
    (selectedOption: SelectedOptions) => {
      const newFilters = [...filters];
      const index = newFilters.findIndex(
        (e) => e.key === selectedOption.key && e.field === selectedOption.field
      );
      newFilters.splice(index, 1);
      emitFiltersChange(newFilters);
    },
    [emitFiltersChange, filters]
  );

  useEffect(() => {
    setFilters(selectedOptions || []);
  }, [selectedOptions]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        flex: 1,
        minWidth: 0,
        ...containerSx,
      }}
    >
      {enableSearch && (
        <TextField
          value={searchValue}
          onChange={(e) => onValueChange?.(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <Iconify icon="material-symbols:search-rounded" />
              </InputAdornment>
            ),
          }}
          // TODO: add debounce
          sx={{
            minWidth: 320,
            '& .MuiOutlinedInput-root': {
              height: 30,
              '& fieldset': {
                borderWidth: '1px !important',
              },
            },
            '& .MuiInputBase-input': {
              height: '1.4375em',
              padding: '3.5px 14px 3.5px 0',
            },
            ...searchFieldSx,
          }}
          placeholder={t('list.search')}
        />
      )}
      {enableFilter && options && (
        <>
          <Box sx={{ flexShrink: 0 }}>
            <FilterDropdown
              buttonLabel={t('filters.buttons.filter')}
              cancelLabel={t('filters.buttons.cancel')}
              applyLabel={t('filters.buttons.apply')}
              options={options}
              selectedOptions={filters}
              onOptionsChanged={(newOptions, touchedFields) => {
                emitFiltersChange(newOptions, touchedFields);
              }}
              // Match MUI `size="small"` date range dropdown height.
              buttonDimensions={{ height: 30 }}
              enableFieldBulkActions={enableFieldBulkActions}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flex: 1,
            }}
          >
            {filters.map((selectedOption) => (
                <Label
                  variant="soft"
                  color="default"
                  sx={{
                    color: 'text.primary',
                    bgcolor: 'action.selected',
                    height: 30,
                    maxWidth:
                      options[selectedOption.field]?.maxWidth ?? DEFAULT_FILTER_CHIP_MAX_WIDTH,
                    minWidth: 0,
                    overflow: 'hidden',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  key={`${selectedOption.field}:${selectedOption.key}`}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ minWidth: 0, flex: 1 }}
                      title={selectedOption.label}
                    >
                      {selectedOption.label}
                    </Typography>
                    {selectedOption.levelValue && (
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.125,
                          borderRadius: 999,
                          bgcolor: 'background.paper',
                          color: 'text.secondary',
                          flexShrink: 0,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {formatAuthorizationLevel(selectedOption.levelValue)}
                        </Typography>
                      </Box>
                    )}
                    {selectedOption.accreditationBodyLabel && (
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.125,
                          borderRadius: 999,
                          bgcolor: 'background.paper',
                          color: 'text.secondary',
                          flexShrink: 0,
                          maxWidth: 120,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={selectedOption.accreditationBodyLabel}
                        >
                          {selectedOption.accreditationBodyLabel}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <Fab
                    color="default"
                    variant="soft"
                    sx={{
                      width: 20,
                      height: 20,
                      minHeight: 0,
                      bgcolor: 'action.disabled',
                      ml: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Iconify
                      icon="ic:round-close"
                      width={16}
                      onClick={() => onOptionDelete(selectedOption)}
                    />
                  </Fab>
                </Label>
            ))}
          </Box>

          {filters.length > 0 && (
            <Button
              variant="text"
              sx={{ height: 30, flexShrink: 0 }}
              onClick={() => emitFiltersChange([])}
            >
              {t('filters.buttons.clear')}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};
